/* TinyBricks — core behaviour.
 * Vanilla custom elements. No framework, no jQuery.
 * Motion primitives used across the theme: rail-snap · card-lift · count-up.
 * All three collapse under prefers-reduced-motion.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ helpers -- */
  const TB = (window.TinyBricks = window.TinyBricks || {});

  // Indian grouping: 12,34,567 — falls back gracefully for other locales.
  function groupDigits(whole) {
    if (whole.length <= 3) return whole;
    return whole
      .replace(/\B(?=(\d{2})+(?!\d)(?=\d{3}))/g, ',')
      .replace(/(\d)(?=(\d{3})$)/, '$1,');
  }

  /* Mirrors Shopify's `money` filter closely enough that a price computed here
     matches the one Liquid rendered for the same amount.
     The no-decimals forms ROUND rather than truncate, because that is what
     Shopify does — truncating put this a rupee under the Liquid figure on any
     total with a fractional part, which is exactly where a discounted bundle
     lands. Any price shown next to a Liquid-rendered one depends on this. */
  TB.formatMoney = function (cents) {
    const format = TB.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withComma = groupDigits(parts[0]);
    const frac = parts[1];
    const rounded = groupDigits(String(Math.round(cents / 100)));
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withComma + '.' + frac)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, rounded)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma + ',' + frac)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, rounded);
  };

  TB.toast = function (message, tone) {
    const region = document.getElementById('toast-region');
    if (!region || !message) return;
    const el = document.createElement('p');
    el.className = 'toast' + (tone ? ' toast--' + tone : '');
    el.textContent = message;
    region.appendChild(el);
    window.setTimeout(function () {
      el.remove();
    }, 4200);
  };

  TB.fetchSection = function (url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (res) {
      if (!res.ok) throw new Error('Section request failed: ' + res.status);
      return res.text();
    });
  };

  TB.parseHTML = function (text) {
    return new DOMParser().parseFromString(text, 'text/html');
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, video[controls]';

  TB.trapFocus = function (container, onEscape) {
    const previous = document.activeElement;
    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (typeof onEscape === 'function') onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeydown);
    return function release() {
      document.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  };

  /* The lock goes on <html>, not <body>. base.css sets `overflow-x: clip` on
     html, and the viewport only takes its overflow from body when html's own
     overflow is visible on BOTH axes — so with clip set there, html is the
     scroll container and `body { overflow: hidden }` clipped nothing while the
     page carried on scrolling behind the open drawer.

     A class rather than an inline style, so it also sits out of reach of
     GoKwik, which writes overflow onto body's style attribute. Counted, so a
     lightbox opened over the drawer does not unlock the page when it closes. */
  let scrollLocks = 0;
  TB.lockScroll = function (lock) {
    scrollLocks = Math.max(0, scrollLocks + (lock ? 1 : -1));
    document.documentElement.classList.toggle('is-scroll-locked', scrollLocks > 0);
  };

  /* GoKwik's script writes `overflow: auto` inline on <body> shortly after
     load. A body that scrolls is a scroll container, and that silently kills
     `position: sticky` for everything inside it — the pinned product gallery
     included. Strip only that value: `hidden` is left alone so both our
     lockScroll above and GoKwik's own modal lock keep working. */
  (function keepStickyAlive() {
    const body = document.body;
    if (!body || typeof MutationObserver === 'undefined') return;
    function clearStrayOverflow() {
      if (scrollLocks === 0 && body.style.overflow === 'auto') {
        body.style.removeProperty('overflow');
      }
    }
    clearStrayOverflow();
    new MutationObserver(clearStrayOverflow).observe(body, {
      attributes: true,
      attributeFilter: ['style'],
    });
  })();

  /* --------------------------------------------------- announcement bar -- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.key = 'tb-announce-dismissed';
      if (this.dataset.dismissible === 'true' && localStorage.getItem(this.key) === this.dataset.signature) {
        this.remove();
        return;
      }
      const close = this.querySelector('[data-announce-close]');
      if (close) {
        close.addEventListener('click', () => {
          try {
            localStorage.setItem(this.key, this.dataset.signature || '1');
          } catch (e) {
            /* private mode — dismissal just won't persist */
          }
          this.remove();
          document.dispatchEvent(new CustomEvent('tb:announce-dismissed'));
        });
      }
    }

  }
  customElements.define('tb-announcement', AnnouncementBar);

  /* --------------------------------------------------------- offer timer -- */
  /* The window is stamped once per visitor and kept in localStorage, so the
     clock carries on falling from page to page instead of resetting to the
     full window on every navigation. Storage can throw (private mode) or be
     wiped between visits; either way the visitor simply gets a fresh window
     rather than a broken strip.

     What is stored is the moment the window OPENED, not the moment it closes.
     That is what makes the repeat possible: the deadline is start + duration,
     and the next window is due at start + cycle, so one number answers both
     "how long is left" and "is this visitor due another run". */
  class OfferTimer extends HTMLElement {
    connectedCallback() {
      const minutes = parseInt(this.dataset.minutes, 10);
      if (!minutes || minutes <= 0) {
        this.remove();
        return;
      }
      const key = this.dataset.key || 'tb-offer-start';
      const window_ms = minutes * 60 * 1000;
      const resetDays = parseInt(this.dataset.resetDays, 10);
      const cycle_ms = resetDays > 0 ? resetDays * 86400000 : 0;
      const now = Date.now();

      let start = parseInt(this.read(key), 10);
      /* A start in the future is a clock change or a stale key. A start older
         than the cycle means this visitor has served their wait and is due a
         fresh window. Either way, open one now. */
      const unusable = Number.isNaN(start) || start > now;
      const due = cycle_ms > 0 && now - start >= cycle_ms;
      if (unusable || due) {
        start = now;
        this.write(key, String(start));
      }
      const deadline = start + window_ms;

      this.fields = {
        hours: this.querySelector('[data-ot-hours]'),
        minutes: this.querySelector('[data-ot-minutes]'),
        seconds: this.querySelector('[data-ot-seconds]')
      };

      const tick = () => {
        const diff = deadline - Date.now();
        if (diff <= 0) {
          this.expire();
          return;
        }
        const s = Math.floor(diff / 1000);
        this.set('hours', Math.floor(s / 3600));
        this.set('minutes', Math.floor((s % 3600) / 60));
        this.set('seconds', s % 60);
      };
      tick();
      if (this.dataset.state === 'running') this.timer = window.setInterval(tick, 1000);
    }

    /* Nothing left to count. If the merchant gave the strip no ended message
       it comes out of the page entirely — a stopped 00:00:00 is worse than no
       timer at all. */
    expire() {
      window.clearInterval(this.timer);
      if (!this.querySelector('[data-ot-expired]')) {
        this.remove();
        document.dispatchEvent(new CustomEvent('tb:announce-dismissed'));
        return;
      }
      this.dataset.state = 'expired';
    }

    set(key, value) {
      const el = this.fields[key];
      if (el) el.textContent = String(value).padStart(2, '0');
    }

    read(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }

    write(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        /* private mode — the window just restarts on the next page */
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }
  }
  customElements.define('tb-offer-timer', OfferTimer);

  /* ---------------------------------------------------------- header ----- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      this.banner = this.querySelector('.announce');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      this.setRetractDistance();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', () => this.setRetractDistance(), { passive: true });
      document.addEventListener('tb:announce-dismissed', () => this.setRetractDistance());
    }

    /* Retracting slides the whole shell up by the banner's height, so anything
       ordered above the offer timer is what gets hidden. Clamping the distance
       to that strip's own offset means the timer can never be the thing that
       scrolls away: put it first in the group and the shell simply stops
       retracting rather than taking the clock off-screen with it. */
    setRetractDistance() {
      const banner = this.banner ? this.banner.offsetHeight : 0;
      const pinned = this.querySelector('[data-keep-visible]');
      /* Measured off the boxes rather than offsetTop: the shell is a sticky —
         and therefore positioned — element, so its children's offsetTop is
         already relative to it, and the two are not in the same coordinate
         space. Both rects move together under the retract transform, so the
         gap between them stays right at any scroll position. */
      const ceiling = pinned
        ? pinned.getBoundingClientRect().top - this.getBoundingClientRect().top
        : banner;
      const h = Math.max(0, Math.min(banner, ceiling));
      this.style.setProperty('--retract', '-' + h + 'px');
      document.documentElement.style.setProperty('--header-offset', this.offsetHeight + 'px');
    }

    onScroll() {
      const y = window.scrollY;
      if (this.header) this.header.dataset.scrolled = y > 8 ? 'true' : 'false';
      const wantsRetract = this.header ? this.header.dataset.retract === 'true' : false;
      if (wantsRetract && this.banner) {
        const goingDown = y > this.lastY && y > 120;
        this.dataset.retracted = goingDown ? 'true' : 'false';
      }
      this.lastY = y;
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('tb-sticky-header', StickyHeader);

  /* ----------------------------------------------------------- drawer ---- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target.classList.contains('drawer__scrim')) {
          this.close();
        }
      });
      const id = this.id;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-drawer-open="' + id + '"]');
        if (trigger) {
          event.preventDefault();
          this.open(trigger);
        }
      });
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      TB.lockScroll(true);
      this.release = TB.trapFocus(this, () => this.close());
      window.requestAnimationFrame(() => {
        const first = this.querySelector('[data-drawer-autofocus]') || this.querySelector(FOCUSABLE);
        if (first) first.focus();
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      TB.lockScroll(false);
      if (this.release) this.release();
      this.release = null;
    }
  }
  customElements.define('tb-drawer', Drawer);

  /* ------------------------------------------------------------- rail ---- */
  /* Scroll-snap track with arrow + dot controls. The last card deliberately
     peeks past the content column; the page itself never scrolls sideways
     because html/body are overflow-x: clip and the rail owns its scroller. */
  class Rail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.dots = Array.from(this.querySelectorAll('[data-rail-dot]'));
      this.items = Array.from(this.track.children).filter((el) => el.dataset.railItem !== undefined);

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.onScroll = this.debounce(() => this.sync(), 80);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.sync();

      if (this.dataset.autoplay === 'true' && !reduceMotion.matches && this.items.length > 1) {
        this.startAutoplay();
      }
    }

    debounce(fn, wait) {
      let t;
      return function () {
        window.clearTimeout(t);
        t = window.setTimeout(fn, wait);
      };
    }

    get index() {
      let closest = 0;
      let min = Infinity;
      this.items.forEach((el, i) => {
        const d = Math.abs(el.offsetLeft - this.track.scrollLeft - this.track.offsetLeft);
        if (d < min) {
          min = d;
          closest = i;
        }
      });
      return closest;
    }

    step(dir) {
      this.goTo(Math.min(this.items.length - 1, Math.max(0, this.index + dir)));
      this.stopAutoplay();
    }

    goTo(i) {
      const target = this.items[i];
      if (!target) return;
      this.track.scrollTo({
        left: target.offsetLeft - this.track.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    sync() {
      const i = this.index;
      this.dots.forEach((dot, di) => dot.setAttribute('aria-current', di === i ? 'true' : 'false'));
      const atStart = this.track.scrollLeft <= 4;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }

    startAutoplay() {
      const interval = (parseInt(this.dataset.autoplayInterval, 10) || 6) * 1000;
      const tick = () => {
        const next = this.index + 1 >= this.items.length ? 0 : this.index + 1;
        this.goTo(next);
      };
      this.timer = window.setInterval(tick, interval);
      this.addEventListener('pointerenter', () => this.pause());
      this.addEventListener('pointerleave', () => this.resume(interval, tick));
      this.addEventListener('focusin', () => this.pause());
    }

    pause() {
      window.clearInterval(this.timer);
    }

    resume(interval, tick) {
      if (this.stopped) return;
      window.clearInterval(this.timer);
      this.timer = window.setInterval(tick, interval);
    }

    stopAutoplay() {
      this.stopped = true;
      window.clearInterval(this.timer);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }
  }
  customElements.define('tb-rail', Rail);

  /* -------------------------------------------------------- countdown ---- */
  class Countdown extends HTMLElement {
    connectedCallback() {
      const target = Date.parse(this.dataset.target);
      if (Number.isNaN(target)) return;
      this.fields = {
        days: this.querySelector('[data-cd-days]'),
        hours: this.querySelector('[data-cd-hours]'),
        minutes: this.querySelector('[data-cd-minutes]'),
        seconds: this.querySelector('[data-cd-seconds]')
      };
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          this.dataset.state = 'live';
          window.clearInterval(this.timer);
          return;
        }
        const s = Math.floor(diff / 1000);
        this.set('days', Math.floor(s / 86400));
        this.set('hours', Math.floor((s % 86400) / 3600));
        this.set('minutes', Math.floor((s % 3600) / 60));
        this.set('seconds', s % 60);
      };
      tick();
      this.timer = window.setInterval(tick, 1000);
    }

    set(key, value) {
      const el = this.fields[key];
      if (el) el.textContent = String(value).padStart(2, '0');
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }
  }
  customElements.define('tb-countdown', Countdown);

  /* ------------------------------------------------------ nav dropdown --- */
  document.addEventListener('DOMContentLoaded', function () {
    const items = document.querySelectorAll('[data-nav-item]');
    items.forEach(function (item) {
      const trigger = item.querySelector('[data-nav-trigger]');
      if (!trigger) return;
      const open = function (state) {
        item.dataset.open = state ? 'true' : 'false';
        trigger.setAttribute('aria-expanded', state ? 'true' : 'false');
      };
      item.addEventListener('pointerenter', function () {
        open(true);
      });
      item.addEventListener('pointerleave', function () {
        open(false);
      });
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        open(item.dataset.open !== 'true');
      });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) open(false);
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          open(false);
          trigger.focus();
        }
      });
    });

    /* ------------------------------------------------- quantity stepper -- */
    document.addEventListener('click', function (event) {
      const btn = event.target.closest('[data-qty-step]');
      if (!btn) return;
      const wrap = btn.closest('[data-qty]');
      const input = wrap && wrap.querySelector('input');
      if (!input) return;
      // A disabled field is not the live quantity — a bundle pack is submitting
      // through items[i][quantity] instead. The buttons sit outside the input,
      // so nothing stops a click reaching here and moving a number that no
      // longer feeds the cart.
      if (input.disabled) return;
      const step = parseInt(btn.dataset.qtyStep, 10);
      const min = parseInt(input.min || '1', 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + step));
      if (next === parseInt(input.value, 10)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    /* ------------------------------------------------------------ share -- */
    document.addEventListener('click', function (event) {
      const btn = event.target.closest('[data-share]');
      if (!btn) return;
      const url = btn.dataset.share || window.location.href;
      if (navigator.share) {
        navigator.share({ url: url, title: document.title }).catch(function () {});
        return;
      }
      navigator.clipboard.writeText(url).then(function () {
        TB.toast(btn.dataset.shareCopied || 'Link copied');
      });
    });

    /* ------------------------------- accordion: one open at a time (opt) -- */
    document.querySelectorAll('[data-accordion-exclusive]').forEach(function (group) {
      group.addEventListener('toggle', function (event) {
        const item = event.target;
        if (item.tagName !== 'DETAILS' || !item.open) return;
        group.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== item) other.open = false;
        });
      }, true);
    });
  });

  /* ------------------------------------------------- recently viewed ----- */
  TB.recordRecentlyViewed = function (id) {
    if (!id) return;
    try {
      const key = 'tb-recently-viewed';
      const list = JSON.parse(localStorage.getItem(key) || '[]').filter(function (v) {
        return v !== id;
      });
      list.unshift(id);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (e) {
      /* storage unavailable — the section simply renders nothing */
    }
  };

  TB.getRecentlyViewed = function () {
    try {
      return JSON.parse(localStorage.getItem('tb-recently-viewed') || '[]');
    } catch (e) {
      return [];
    }
  };
})();
